import { useCustom } from "@refinedev/core";
import { AlertTriangle, ClipboardList, Search } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";

import { Combo } from "@/components/Combo";
import { ApiError, query } from "@/lib/api";
import { cancelVerdict } from "@/lib/cancel";
import { errorMessage, stamp, taskWord } from "@/lib/format";
import { pageData } from "@/lib/loaded";
import type {
  FbsTaskBody,
  FbsTaskCard,
  FbsTaskOzon,
  FbsTaskRow,
  FbsTaskStep,
  FbsTaskWb,
  FbsTasks,
  MovePoint,
} from "@/lib/providers";
import { Cell } from "@/pages/FbsBoxesPage";
import {
  moveSerial,
  stepEnds,
  stepTitle,
  taskNumber,
  znakMismatch,
} from "@/lib/task";
import { useSessionGuard } from "@/lib/useSessionGuard";

const FIELD =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[13px] focus:border-border-strong";

/**
 * Раздел «FBS Задача»: СПИСОК сборки и КАРТОЧКА одной её единицы.
 *
 * Экран один, а вопросов два, и решает между ними адрес: `/fbs-task` — «что
 * склад собирает и где», `/fbs-task/1213` — «что было с этой задачей».
 *
 * Список заведён 09.09.26 по задаче владельца. До него раздел открывался
 * ТОЛЬКО по номеру, а номер берут из ленты движений — то есть, придя из меню,
 * человек упирался в пустое поле и не мог ответить ни на что. Вопрос «что
 * сейчас в работе в Ростове» задают чаще, чем «что было с задачей 1213», и
 * первым он идёт по времени: список ведёт в карточку, а не наоборот. Тот же
 * список у соседа `/claude/project/fbswb` (`/wh-tasks`), и форма отбора взята
 * оттуда.
 *
 * Карточка: сюда приходят из ленты движений — строка сказала «задача 1213 ·
 * упаковка», и дальше нужен весь путь. Раздел «Серия» отвечает на «что с
 * вещью», «Короба» — на «что везти», а этот — на «ЧТО ЗА РАБОТОЙ стоит это
 * перемещение».
 *
 * Четыре блока, и они идут в порядке вопросов:
 *
 *     задача     номер, статус, площадка, серия, знак, город и товар
 *     подбор     кто снял вещь с полки, каким перемещением, откуда и куда
 *     упаковка   кто уложил её в короб отгрузки, тем же набором полей
 *     задание    что с этим заказом у маркетплейса — ВБ или Ozon
 *
 * Номер в адресе НАШ (`fbs.fbs_task.id`), а не номер задания ВБ или
 * отправления Ozon: складская работа общая на обе площадки, у неё один адрес, и
 * искать вещь по чужому номеру пришлось бы, зная сначала, чей он.
 */
export function FbsTaskPage() {
  const { taskId = "" } = useParams();
  const navigate = useNavigate();
  // Поле поиска в состоянии, запрос — в АДРЕСЕ: карточку пересылают ссылкой, и
  // у сменщика она обязана открыться такой же. То же решение, что у серии.
  const [draft, setDraft] = useState(taskId);
  useEffect(() => setDraft(taskId), [taskId]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const next = taskNumber(draft);
    if (next === null) return;
    navigate(`/fbs-task/${next}`);
  };

  const asked = taskNumber(taskId);
  const taskQuery = useCustom<FbsTaskCard>({
    url: `/api/fbs/tasks/${encodeURIComponent(taskId)}`,
    method: "get",
    queryOptions: { enabled: asked !== null },
  });

  const loaded = taskQuery.result.data;
  // Задачей считается только payload с задачей: `result.data` у Refine
  // существует и до загрузки — пустым объектом.
  const item = loaded && loaded.task && typeof loaded.task.id === "number" ? loaded : null;
  const error = taskQuery.query.error;
  useSessionGuard(error);

  return (
    <main
      className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 md:p-5"
      data-component="FbsTaskPage"
      data-annot="fbs задача"
    >
      <form
        onSubmit={submit}
        data-annot="поиск задачи"
        className="flex flex-wrap items-center gap-2"
      >
        <label className="text-[12.5px] text-fg-muted" htmlFor="fbs-task-number">
          Задача FBS
        </label>
        <input
          id="fbs-task-number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          inputMode="numeric"
          placeholder="номер складской задачи"
          className={FIELD + " tnum w-[200px]"}
        />
        <button
          type="submit"
          className="flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] bg-fg px-3 text-[13px] font-medium text-bg"
        >
          <Search size={13} />
          Открыть
        </button>
      </form>

      {!taskId ? (
        <TaskList />
      ) : error ? (
        <Failed taskId={taskId} error={error} />
      ) : !item ? (
        <p className="text-[13px] text-fg-muted">Загружаю…</p>
      ) : (
        <>
          <Head card={item} />
          <div className="grid gap-4 md:grid-cols-2">
            <Step step={item.task.pick} />
            <Step step={item.task.pack} />
          </div>
          <Source card={item} />
        </>
      )}
    </main>
  );
}

/** Сколько строк добавляет одно нажатие «показать ещё». Ровно страница API. */
const PAGE = 100;

/**
 * СПИСОК складских задач: что склад собирает прямо сейчас и где.
 *
 * Стоит на месте бывшей заглушки «наберите номер». Заглушка была честной, но
 * бесполезной: номер задачи берут из ленты движений, и человек, пришедший в
 * раздел из меню, не мог сделать ничего.
 *
 * **Отбор по ГОРОДУ — главный** (он назван в задаче владельца явно). Склад у
 * нас в трёх городах и работа в них разная: замер 09.09.26 — Ростов 1 390
 * задач, Воронеж 756, Самара 10. «Что собирают» без города — не вопрос.
 *
 * Отбор живёт в АДРЕСЕ, а не в состоянии: «задачи Ростова, ждут подбора» —
 * ссылка, которую отправляют сменщику, и открыться у него она обязана такой
 * же. То же решение, что у списка коробов и у раздела «Товары».
 *
 * Числа переключателей считает СЕРВИС по отбору без своего поля — «сколько
 * будет, если переключить». Второго счёта на экране нет: посчитай мы города по
 * показанной странице, «Воронеж 12» означало бы «двенадцать на этом экране», а
 * не «756 в городе».
 */
function TaskList() {
  const [params, setParams] = useSearchParams();
  const city = params.get("city") ?? "";
  const platform = params.get("platform") ?? "";
  const status = params.get("status") ?? "";
  const limitParam = params.get("n") ?? "";

  /** Правит ОДИН ключ адреса, не трогая остальные. */
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    // Смена любого отбора возвращает список к первой странице: поднятый
    // потолок относился к прежнему отбору и обещал бы не те строки.
    if (key !== "n") next.delete("n");
    setParams(next, { replace: true });
  };

  const url = useMemo(
    () =>
      `/api/fbs/tasks${query({
        city_id: city || undefined,
        platform: platform || undefined,
        status: status || undefined,
        limit: limitParam || undefined,
      })}`,
    [city, platform, status, limitParam],
  );

  const listQuery = useCustom<FbsTasks>({ url, method: "get" });
  useSessionGuard(listQuery.query.error);

  // Списком считается только payload, в котором ДЕЙСТВИТЕЛЬНО есть строки:
  // `result.data` у Refine существует и до загрузки — пустым объектом, и
  // обращение к вложенным полям на нём роняет экран целиком (разбор — в
  // `lib/loaded.ts`).
  const list = pageData(listQuery.result.data, "rows");
  const busy = listQuery.query.isFetching;
  const error = listQuery.query.error;

  if (error) {
    const off = error instanceof ApiError && error.status === 503;
    return (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-6 text-center"
      >
        <AlertTriangle size={20} className="text-err" />
        <p className="text-[13.5px]">
          {off
            ? "Базы задач FBS в этой полосе нет: раздел работает только на боевой"
            : errorMessage(error)}
        </p>
      </div>
    );
  }

  if (!list) return <p className="text-[13px] text-fg-muted">Считаю…</p>;

  const rows = list.rows;
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3" data-annot="список задач">
      <div className="flex flex-wrap items-center gap-2" data-annot="фильтр">
        {/* Город — ПЕРВЫМ и переключателем, а не строкой списка: городов три,
            и выбор между ними это и есть главный вопрос списка. */}
        <div
          data-annot="город"
          className="inline-flex flex-wrap rounded-[var(--radius-ui)] border border-border p-0.5"
        >
          <Chip active={!city} onClick={() => set("city", "")}>
            все города
          </Chip>
          {list.cities.map((one) => (
            <Chip
              key={one.id}
              active={city === String(one.id)}
              onClick={() => set("city", String(one.id))}
            >
              {one.name} <span className="tnum opacity-60">{one.tasks}</span>
            </Chip>
          ))}
        </div>

        <div
          data-annot="площадка"
          className="inline-flex rounded-[var(--radius-ui)] border border-border p-0.5"
        >
          <Chip active={!platform} onClick={() => set("platform", "")}>
            обе
          </Chip>
          {list.platforms.map((one) => (
            <Chip
              key={one.code}
              active={platform === one.code}
              onClick={() => set("platform", one.code ?? "")}
            >
              {one.name} <span className="tnum opacity-60">{one.tasks}</span>
            </Chip>
          ))}
        </div>

        {/* Статус — списком, а не чипами: их шесть, и в ряд они заняли бы
            строку целиком. Порядок — ход работы, его задаёт сервис. */}
        <Combo
          label="статус"
          value={status}
          onChange={(v) => set("status", v)}
          width={200}
          options={[
            { value: "", label: "любой" },
            ...list.statuses.map((one) => ({
              value: one.code ?? "",
              label: one.name ?? one.code ?? "",
              meta: String(one.tasks),
            })),
          ]}
        />

        <span className="ml-auto text-[12.5px] text-fg-muted" aria-busy={busy}>
          {list.total} {taskWord(list.total)}
        </span>
      </div>

      {/* Отказ CRM гасит ИМЕНА, а не список: задачи — наш учёт, и молчание
          чужого справочника не повод прятать работу склада. */}
      {!list.sources.crm ? (
        <p className="rounded-[var(--radius-ui)] border border-warn/60 bg-warn-bg px-3 py-1.5 text-[12.5px]">
          CRM не ответила: в строках стоят номера вместо людей, товаров и
          городов.
        </p>
      ) : null}

      {rows.length === 0 ? (
        <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
          Под этот отбор нет ни одной задачи.
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
          <table data-annot="таблица" className="w-full border-collapse text-[13px]">
            <caption className="sr-only">
              Складские задачи FBS: номер, статус, товар, город, серия, знак и
              кто её делал
            </caption>
            <thead className="hidden md:table-header-group">
              <tr>
                {[
                  "Задача",
                  "Статус",
                  "Товар",
                  "Город",
                  "Серия",
                  "Знак",
                  "Подбор",
                  "Упаковка",
                ].map((title) => (
                  <th
                    key={title}
                    scope="col"
                    className="sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated px-2.5 py-2 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-fg"
                  >
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="block md:table-row-group">
              {rows.map((row) => (
                <tr
                  key={row.id}
                  // Адрес замечания — НОМЕР задачи, а не позиция в списке:
                  // «строка 2040» находится на складе, «третья сверху» — нет,
                  // порядок меняется со следующей сборкой.
                  data-annot={`строка ${row.id}`}
                  className="mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover"
                >
                  <Cell label="Задача">
                    <span className="flex flex-col">
                      <Link
                        to={`/fbs-task/${row.id}`}
                        className="tnum font-medium text-fg hover:text-accent-text"
                      >
                        {row.id}
                      </Link>
                      <span className="text-[11px] text-fg-muted">
                        {row.created_at ? stamp(row.created_at) : "—"}
                      </span>
                    </span>
                  </Cell>
                  <Cell label="Статус">
                    <span className="flex flex-col">
                      <span>{row.status_name || row.status}</span>
                      <span className="text-[11px] text-fg-muted">
                        {row.platform || "—"}
                      </span>
                    </span>
                  </Cell>
                  <Cell label="Товар">
                    <ProductCell row={row} />
                  </Cell>
                  <Cell label="Город">
                    {row.city?.short || row.city?.name || "—"}
                  </Cell>
                  <Cell label="Серия" align="tnum">
                    {row.serial ? (
                      <Link
                        to={`/serial/${row.serial}`}
                        className="hover:underline"
                      >
                        {row.serial}
                      </Link>
                    ) : (
                      <span className="text-fg-muted">—</span>
                    )}
                  </Cell>
                  {/* Знак — с его СОБСТВЕННОЙ серией: номер знака сам по себе
                      на складе ничего не значит, вопрос всегда «чья это
                      наклейка». */}
                  <Cell label="Знак" align="tnum">
                    <ZnakCell row={row} />
                  </Cell>
                  <Cell label="Подбор">
                    <WhoCell who={row.pick.user} at={row.pick.done_at} id={row.pick.user_id} />
                  </Cell>
                  <Cell label="Упаковка">
                    <WhoCell who={row.pack.user} at={row.pack.done_at} id={row.pack.user_id} />
                  </Cell>
                </tr>
              ))}
            </tbody>
          </table>
          {list.shown < list.total ? (
            <div className="border-t border-border p-3 text-center">
              <button
                type="button"
                disabled={busy}
                onClick={() => set("n", String(list.shown + PAGE))}
                className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-60"
              >
                {busy
                  ? "Гружу…"
                  : `Показать ещё ${Math.min(PAGE, list.total - list.shown)}`}
              </button>
              <p className="mt-1.5 text-[11.5px] text-fg-muted">
                Показано {list.shown} из {list.total}
              </p>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/** Кнопка-чип отбора. Нажатая помечена `aria-pressed`, а не только цветом. */
function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        "h-7 rounded-[calc(var(--radius-ui)-2px)] px-2.5 text-[12.5px] " +
        (active
          ? "bg-accent-dim font-medium text-accent-text"
          : "text-fg-muted hover:text-fg")
      }
    >
      {children}
    </button>
  );
}

/** Товар строки: баркод сверху, имя под ним. Баркод ведёт в свой раздел. */
function ProductCell({ row }: { row: FbsTaskRow }) {
  if (!row.card) {
    return (
      <span className="text-[12px] text-fg-muted">
        {row.card_id ? `карточка ${row.card_id}` : "не заведён"}
      </span>
    );
  }
  return (
    <span className="flex max-w-[280px] flex-col">
      <Link
        to={`/barcode/${row.card.barcode}`}
        className="tnum text-[12.5px] hover:text-accent-text"
      >
        {row.card.barcode}
      </Link>
      <span className="truncate text-[11px] text-fg-muted" title={row.card.name}>
        {row.card.size ? `${row.card.size} · ` : ""}
        {row.card.name}
      </span>
    </span>
  );
}

/**
 * Знак строки и серия, на которой он лежит.
 *
 * Обе цифры вместе, потому что порознь бесполезны: номер знака ищут в CRM, а
 * серия отвечает на «чья наклейка». Расхождение подписано словами и не
 * покрашено в красное — разбор в докстринге `Znak` карточки.
 */
function ZnakCell({ row }: { row: FbsTaskRow }) {
  if (!row.znak_id) return <span className="text-fg-muted">—</span>;
  const other = znakMismatch(row);
  return (
    <span className="flex flex-col">
      <span>{row.znak_id}</span>
      {row.znak_serial ? (
        <span className="text-[11px] text-fg-muted">
          <Link to={`/serial/${row.znak_serial}`} className="hover:underline">
            {row.znak_serial}
          </Link>
          {other ? " · не та вещь" : ""}
        </span>
      ) : null}
    </span>
  );
}

/** Половина работы в строке списка: кто и когда закончил. */
function WhoCell({
  who,
  at,
  id,
}: {
  who: string | null;
  at: string | null;
  id: number | null;
}) {
  if (!who && !at && !id) return <span className="text-fg-muted">—</span>;
  return (
    <span className="flex flex-col">
      <span className="text-[12.5px]">{who || (id ? `№${id}` : "—")}</span>
      <span className="tnum text-[11px] text-fg-muted">
        {at ? stamp(at) : "в работе"}
      </span>
    </span>
  );
}

/**
 * Отказ. Три случая, и они чинятся разным: 404 — исправленным номером, 503 —
 * ничем (в песочной полосе баз соседей нет вовсе), прочее — ожиданием.
 */
function Failed({ taskId, error }: { taskId: string; error: unknown }) {
  const missing = error instanceof ApiError && error.status === 404;
  const off = error instanceof ApiError && error.status === 503;
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-6 text-center"
    >
      <AlertTriangle size={20} className="text-err" />
      <p className="text-[13.5px]">
        {missing
          ? `Складской задачи ${taskId} нет`
          : off
            ? "Базы задач FBS в этой полосе нет: раздел работает только на боевой"
            : errorMessage(error)}
      </p>
    </div>
  );
}

/** Номер, статус, площадка — и вещь, которую эта задача собирала. */
function Head({ card }: { card: FbsTaskCard }) {
  const task = card.task;
  const done = task.cancelled_at ? "cancelled" : task.status;
  return (
    <header className="flex flex-wrap items-start gap-4" data-annot="шапка задачи">
      {card.card?.image_url ? (
        <img
          src={card.card.image_url}
          alt=""
          referrerPolicy="no-referrer"
          className="h-[132px] w-[96px] flex-none rounded-[var(--radius-ui)] border border-border object-cover"
          onError={(e) => {
            e.currentTarget.style.visibility = "hidden";
          }}
        />
      ) : null}
      <div className="min-w-[240px] flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="tnum flex items-center gap-2 text-[22px] font-semibold tracking-[-0.02em]">
            <ClipboardList size={18} className="text-fg-muted" />
            Задача {task.id}
          </h1>
          <span
            data-annot="статус задачи"
            className={
              "rounded border px-1.5 py-0.5 text-[11.5px] font-medium " +
              (done === "cancelled"
                ? "border-err/60 bg-err-bg text-err"
                : done === "packed"
                  ? "border-ok/60 bg-ok-bg text-ok"
                  : "border-border bg-bg-elevated text-fg-muted")
            }
          >
            {task.cancelled_at ? "Отменена" : task.status_name || task.status}
          </span>
          {task.platform ? (
            <span className="rounded border border-border bg-bg-elevated px-1.5 py-0.5 text-[11.5px] font-medium text-fg-muted">
              {task.platform}
            </span>
          ) : null}
        </div>
        <p className="mt-1 text-[13.5px] text-fg-muted">
          {card.card?.name || "Товар на маркетплейсе не заведён"}
        </p>
        <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12.5px]">
          {/* Серия ссылкой: задача — это работа НАД вещью, и разбор «что с ней
              стало» продолжается в её карточке. */}
          {task.serial ? (
            <div className="flex items-baseline gap-1.5">
              <dt className="text-fg-muted">Серия</dt>
              <dd className="tnum">
                <Link to={`/serial/${task.serial}`} className="text-fg hover:underline">
                  {task.serial}
                </Link>
              </dd>
            </div>
          ) : null}
          {card.card?.barcode ? (
            <div className="flex items-baseline gap-1.5">
              <dt className="text-fg-muted">Баркод</dt>
              <dd className="tnum">
                <Link
                  to={`/barcode/${card.card.barcode}`}
                  className="text-fg hover:underline"
                >
                  {card.card.barcode}
                </Link>
              </dd>
            </div>
          ) : null}
          {card.card?.size ? <Pair label="Размер" value={card.card.size} /> : null}
          <Pair
            label="Город"
            value={card.location?.name || (task.location_id ? `№${task.location_id}` : "—")}
          />
          {task.created_at ? (
            <Pair label="Заведена" value={stamp(task.created_at)} mono />
          ) : null}
          {task.znak_id ? <Znak task={task} /> : null}
        </dl>
      </div>
    </header>
  );
}

/**
 * Знак (честный знак) и СЕРИЯ, на которой он лежит по учёту CRM.
 *
 * Требование владельца 09.09.26: «у каждого знака есть своя привязанная серия».
 * Номер знака сам по себе на складе ничего не значит — вопрос всегда «чья это
 * наклейка», и ответ на него `sBarcodeZnak.serial`.
 *
 * Расхождение с серией задачи ПОДПИСАНО, но не покрашено в красное: это не
 * поломка. Наклейку клеит человек, автоподбора знака по серии в сборке нет
 * намеренно, и «иногда кизы перепутываются» — решение владельца 01.09.26 в
 * соседнем `fbs`. Замер 09.09.26: из 1 273 задач со знаком серии разошлись у
 * 708, и во ВСЕХ 708 товар тот же самый — знака от чужого товара нет ни
 * одного. То есть сверка, которая держит, держит; красный цвет тут кричал бы
 * об ошибке там, где её нет.
 */
function Znak({ task }: { task: FbsTaskBody }) {
  const other = znakMismatch(task);
  return (
    <div className="flex items-baseline gap-1.5" data-annot="знак">
      <dt className="text-fg-muted">Знак</dt>
      <dd className="tnum">
        {task.znak_id}
        {task.znak_serial ? (
          <>
            {" · серия "}
            <Link
              to={`/serial/${task.znak_serial}`}
              className="hover:underline"
              title={
                other
                  ? "Знак лежит на другой серии того же товара — наклейки на складе иногда путают"
                  : "Серия, на которой лежит этот знак"
              }
            >
              {task.znak_serial}
            </Link>
            {other ? (
              <span className="ml-1 text-[11px] text-fg-muted">не та вещь</span>
            ) : null}
          </>
        ) : null}
      </dd>
    </div>
  );
}

/**
 * Половина задачи: подбор или упаковка. Обе рисуются ОДНИМ компонентом.
 *
 * Разница между ними только в словах, приехавших от сервиса, и рисовать их
 * двумя блоками значило бы дать «откуда взяли» и «куда положили» разойтись
 * набором полей — а сверять их на экране приходится построчно.
 */
function Step({ step }: { step: FbsTaskStep }) {
  const empty = !step.move_id && !step.done_at && !step.user_id;
  const ends = stepEnds(step);
  const serial = moveSerial(step);
  return (
    <Panel title={stepTitle(step.role_name)} annot={`задача ${step.role}`}>
      {empty ? (
        <p className="text-[13px] text-fg-muted">
          {step.role === "pack" ? "Ещё не упаковано." : "Ещё не подобрано."}
        </p>
      ) : (
        <dl className="flex flex-col gap-1.5 text-[13px]">
          {/* Номер ПЕРЕМЕЩЕНИЯ — тот же, что в строке ленты движений: по нему
              карточка и лента сходятся глазами, без пересчёта. Слово именно
              «Перемещение» (замечание владельца 09.09.26): так операция
              зовётся в CRM и так же зовётся наша дверь `POST /api/move`. */}
          <Row label="Перемещение">
            <span className="tnum">{step.move_id ?? "—"}</span>
          </Row>
          {/* Серия ИЗ САМОГО перемещения, а не из задачи: строка отвечает на
              «что именно переехало». Ссылкой — разбор вещи продолжается в её
              карточке. */}
          <Row label="Серия">
            {serial ? (
              <Link to={`/serial/${serial}`} className="tnum hover:underline">
                {serial}
              </Link>
            ) : (
              <span className="text-fg-muted">—</span>
            )}
          </Row>
          {/* Оба конца, а не один: у перемещения есть «откуда» и «куда», и
              половина ответа заставляла бы гадать вторую. */}
          <Row label="Откуда">
            <PlaceName point={ends.from} />
          </Row>
          <Row label="Куда">
            <PlaceName point={ends.to} />
          </Row>
          <Row label="Кто">
            <span>{step.user || (step.user_id ? `№${step.user_id}` : "—")}</span>
          </Row>
          <Row label="Взял в работу">
            <span className="tnum">{step.claimed_at ? stamp(step.claimed_at) : "—"}</span>
          </Row>
          <Row label="Закончил">
            <span className="tnum">{step.done_at ? stamp(step.done_at) : "—"}</span>
          </Row>
        </dl>
      )}
    </Panel>
  );
}

/**
 * Задание маркетплейса за складской задачей — три взаимоисключающих состояния.
 *
 * Флаг источника показывается ОТДЕЛЬНО от пустоты: «задания нет» (сборка не от
 * маркетплейса) и «зеркало кабинета молчит» — разные ответы, и одно пустое
 * поле на оба заставило бы гадать, какой из них перед глазами.
 */
function Source({ card }: { card: FbsTaskCard }) {
  const task = card.task;
  if (card.wb) return <Wb wb={card.wb} />;
  if (card.ozon) return <Ozon ozon={card.ozon} />;

  const askedWb = task.platform_id === 1;
  const lost = askedWb ? !card.sources.wb : !card.sources.ozon;
  return (
    <Panel title="Задание маркетплейса" annot="задание">
      <p className="text-[13px] text-fg-muted">
        {lost
          ? askedWb
            ? "Зеркало кабинета ВБ прочитать не удалось — это «не знаю», а не «задания нет»."
            : "Зеркало кабинета Ozon прочитать не удалось — это «не знаю», а не «задания нет»."
          : "Задания маркетплейса за этой задачей нет."}
      </p>
    </Panel>
  );
}

/** Задание ВБ: наш статус, взгляд ВБ и поставка, которой оно уехало. */
function Wb({ wb }: { wb: FbsTaskWb }) {
  return (
    <Panel title="Задание Wildberries" annot="задание вб">
      {/* Зазор между колонками ШИРЕ обычного (`md:gap-x-10`): строки здесь
          «подпись — значение», и на узком зазоре значение левой колонки
          сливалось с подписью правой в одну фразу («Передана в доставку
          Передана в доставку»). Замечено на бою 09.09.26. */}
      <dl className="grid gap-1.5 text-[13px] md:grid-cols-2 md:gap-x-10">
        <Row label="Задание">
          <span className="tnum">{wb.order_id ?? wb.row_id}</span>
        </Row>
        <Row label="Артикул ВБ">
          <span className="tnum">{wb.nm_id ?? "—"}</span>
        </Row>
        {/* Статуса ДВА, и путать их нельзя: первый — наш взгляд на задание,
            второй — взгляд ВБ на заказ. Отказы покупателя живут во втором, и
            по нему НЕЛЬЗЯ судить, уехала ли вещь: `canceled_by_client` стоит у
            93 из 1 549 заданий в закрытых поставках, а физически они у ВБ. */}
        <Row label="Статус">
          <span>{wb.status_name || wb.status || "—"}</span>
        </Row>
        <Row label="Статус у ВБ">
          <span>{wb.substatus_name || wb.substatus || "—"}</span>
        </Row>
        <Row label="Заказано">
          <span className="tnum">{wb.ordered_at ? stamp(wb.ordered_at) : "—"}</span>
        </Row>
        <Row label="Поставка">
          <span className="tnum">{wb.supply.id || "—"}</span>
        </Row>
        <Row label="Статус поставки">
          <span>{wb.supply.status_name || wb.supply.status || "—"}</span>
        </Row>
        {/* Подписи дат КОРОТКИЕ и не повторяют статус: «Передана в доставку»
            стоит значением строки выше, и той же подписью рядом читалось бы
            как одно предложение из двух ячеек. */}
        <Row label="Закрыта">
          <span className="tnum">
            {wb.supply.closed_at ? stamp(wb.supply.closed_at) : "—"}
          </span>
        </Row>
        <Row label="Отсканирована">
          <span className="tnum">
            {wb.supply.scanned_at ? stamp(wb.supply.scanned_at) : "—"}
          </span>
        </Row>
        {/* Списание — след автомата `spisanie-wb`: по этой строке видно, что
            вещь уже снята с учёта, и повторно её списывать не надо. */}
        <Row label="Списано">
          <span className="tnum">
            {wb.spisanie_at ? stamp(wb.spisanie_at) : wb.spisanie_id ? "да" : "—"}
          </span>
        </Row>
      </dl>
    </Panel>
  );
}

/** Отправление Ozon: статус заказа и акт перевозки, которым он уехал. */
function Ozon({ ozon }: { ozon: FbsTaskOzon }) {
  return (
    <Panel title="Отправление Ozon" annot="задание ozon">
      <dl className="grid gap-1.5 text-[13px] md:grid-cols-2 md:gap-x-10">
        <Row label="Отправление">
          <span className="tnum">{ozon.posting_number}</span>
        </Row>
        <Row label="Заказ">
          <span className="tnum">{ozon.order_number || "—"}</span>
        </Row>
        <Row label="Статус">
          <span>{ozon.status || "—"}</span>
        </Row>
        <Row label="Подстатус">
          <span>{ozon.substatus || "—"}</span>
        </Row>
        <Row label="Срок отгрузки">
          <span className="tnum">
            {ozon.shipment_date ? stamp(ozon.shipment_date) : "—"}
          </span>
        </Row>
        {/* Акт — ЕДИНСТВЕННЫЙ признак «вещь у Ozon», и потому он здесь, а не
            статус отправления: Ozon отменяет отправление и ПОСЛЕ отгрузки —
            девять таких серий уже уехали в акте (замер 08.09.26). Пустой акт
            значит, что вещь ещё у нас. */}
        <Row label="Акт перевозки">
          <span className="tnum">{ozon.carriage_id ?? "не уехало"}</span>
        </Row>
        <Row label="Статус акта">
          <span>{ozon.carriage_status || "—"}</span>
        </Row>
        <Row label="Отгружено">
          <span className="tnum">
            {ozon.departure_date ? stamp(ozon.departure_date) : "—"}
          </span>
        </Row>
        {/* Отмена — вопрос владельца 09.09.26: «cancel же может быть после
            того, как мы уже её сдали». Статус `cancelled` сам по себе на это
            не отвечает, а от ответа зависит, лежит вещь в коробе или её там
            нет. Оба ответа — кабинета и нашего акта — сводит `cancelVerdict`,
            и он же помечает случай, когда они разошлись. */}
        <Cancel ozon={ozon} />
      </dl>
    </Panel>
  );
}

/** Разбор отмены отправления: успели сдать посылку или нет. */
function Cancel({ ozon }: { ozon: FbsTaskOzon }) {
  const verdict = cancelVerdict(ozon.cancel, ozon.carriage_id !== null);
  if (!verdict) return null;
  // Одной строкой, а не двумя: вердикт и то, чем он подтверждён, читаются
  // только вместе — «отменено ПОСЛЕ отгрузки» без «а акта нет» звучит как
  // обычная отгрузка. Отдельная подпись под второй половиной вдобавок не
  // умещалась в колонку и переносилась.
  return (
    <Row label="Отмена">
      <span className="flex flex-col items-end gap-0.5 text-right md:items-start md:text-left">
        <span
          className={
            "flex items-center gap-1 " +
            (verdict.disputed ? "font-medium text-err" : "")
          }
        >
          {verdict.disputed ? (
            <AlertTriangle size={12} className="flex-none" />
          ) : null}
          {verdict.label}
        </span>
        <span className="text-[11.5px] text-fg-muted">{verdict.note}</span>
      </span>
    </Row>
  );
}

/**
 * Место половины задачи — тем же видом, что в ленте движений: имя, под ним
 * город и группа. Номер короба здесь НЕ вырезается из имени: `sortOrder` в
 * ответе задачи нет, а угадывать номер по цифрам в названии значило бы
 * заводить второе правило имени места рядом с `lib/places.ts`, которое знает
 * его точно.
 *
 * Город печатается отдельно намеренно: имена коробов в CRM несут склад
 * («Невиномыск Невиномыск Короб 11103»), а в справочнике локаций у них Ростов.
 */
function PlaceName({ point }: { point: MovePoint | null }) {
  if (!point || point.place_id === null) {
    return <span className="text-fg-muted">—</span>;
  }
  return (
    <span className="flex flex-col text-right md:text-left">
      <span>{point.name || `Место ${point.place_id}`}</span>
      {point.city || point.group ? (
        <span className="text-[11px] text-fg-muted">
          {[point.city, point.group].filter(Boolean).join(" · ")}
        </span>
      ) : null}
    </span>
  );
}

/** Раздел карточки: заголовок и содержимое в одной рамке. Как у серии. */
function Panel({
  title,
  annot,
  children,
}: {
  title: string;
  annot: string;
  children: React.ReactNode;
}) {
  return (
    <section
      data-annot={annot}
      className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-3"
    >
      <h2 className="mb-2 text-[11px] uppercase tracking-[0.04em] text-fg-muted">
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Строка «подпись — значение» внутри раздела. */
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-1 last:border-0 md:border-0 md:py-0">
      <dt className="text-fg-muted">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Pair({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="text-fg-muted">{label}</dt>
      <dd className={mono ? "tnum" : ""}>{value}</dd>
    </div>
  );
}
